import {
  CostSheetImportReport,
  CostSheetVersionSummary,
  SurveyProjectSummary,
  SurveySubmissionSummary,
  useCostSheetVersions,
  useSurveyEndpoints,
} from "@inrange/building-manager-api-client";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Badge, Button, Dropdown, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";

const CURRENCY_SYMBOLS: Record<string, string> = { GBP: "£", EUR: "€" };

const formatPerKwp = (
  version: CostSheetVersionSummary | null
): string | null => {
  if (!version || version.total_per_kwp == null) return null;
  const symbol = CURRENCY_SYMBOLS[version.currency] ?? `${version.currency} `;
  return `${symbol}${Math.round(version.total_per_kwp)} / kWp`;
};

const findSubmission = (
  project: SurveyProjectSummary,
  kind: "on_site_survey" | "cost_sheet"
): SurveySubmissionSummary | null =>
  (project.submissions || []).find((s) => s.kind === kind) || null;

const submissionStatusBadge = (sub: SurveySubmissionSummary | null) => {
  if (!sub) return <Badge bg="secondary">-</Badge>;
  if (sub.is_submitted) return <Badge bg="success">Submitted</Badge>;
  return (
    <Badge bg="warning" text="dark">
      In progress
    </Badge>
  );
};

interface SiteSelectOption {
  value: string;
  label: string;
}

interface SurveyProjectRowProps {
  endpointId: string;
  project: SurveyProjectSummary;
  siteOptions: { id: string; name: string }[];
  sitesLoading: boolean;
  apiHostname: string;
  // Customer-app origin + endpoint slug parts, used to build the admin-only
  // anonymised-PDF export URL (survey-form.html?mode=anonExport).
  customerAppDomain: string;
  endpointName: string;
  endpointShortcode: string;
  // Link target for the project-name cell; null renders plain text.
  projectNameHref: string | null;
  selectInputIdPrefix: string;
  selectMinWidth: number;
  selectStyles: Record<
    string,
    (base: Record<string, unknown>) => Record<string, unknown>
  >;
}

export const SurveyProjectRow = ({
  endpointId,
  project,
  siteOptions,
  sitesLoading,
  apiHostname,
  customerAppDomain,
  endpointName,
  endpointShortcode,
  projectNameHref,
  selectInputIdPrefix,
  selectMinWidth,
  selectStyles,
}: SurveyProjectRowProps) => {
  const { updateSiteOnProject, adminDeleteProject, importCostSheet } =
    useSurveyEndpoints();
  const [error, setError] = useState<string | null>(null);
  const [importReport, setImportReport] =
    useState<CostSheetImportReport | null>(null);
  const [importVersion, setImportVersion] =
    useState<CostSheetVersionSummary | null>(null);
  const [importErrorMessage, setImportErrorMessage] = useState<string | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isImporting =
    importCostSheet.isLoading &&
    importCostSheet.variables?.projectId === project.id;

  const handleImportFile = async (file: File | undefined) => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setImportReport(null);
    setImportVersion(null);
    setImportErrorMessage(null);
    try {
      const response = await importCostSheet.mutateAsync({
        endpointId,
        projectId: project.id,
        file,
      });
      setImportReport(response.report);
      setImportVersion(response.version ?? null);
    } catch (err) {
      setImportErrorMessage(
        "The cost sheet could not be uploaded. Please try again."
      );
      // Surface the unexpected (non-validation) failure for debugging.
      console.error("Cost sheet import failed", err);
    }
  };

  const selectedSite = project.site_id
    ? siteOptions.find((s) => s.id === project.site_id) || null
    : null;

  const handleChange = async (newSiteId: string | null) => {
    setError(null);
    if (newSiteId === project.site_id) return;
    try {
      await updateSiteOnProject.mutateAsync({
        endpointId,
        projectId: project.id,
        siteId: newSiteId,
      });
    } catch (err) {
      setError(String(err));
    }
  };

  const isPending =
    updateSiteOnProject.isLoading &&
    updateSiteOnProject.variables?.projectId === project.id;

  const isDeleting =
    adminDeleteProject.isLoading &&
    adminDeleteProject.variables?.projectId === project.id;

  const surveySub = findSubmission(project, "on_site_survey");
  const costSub = findSubmission(project, "cost_sheet");
  const hasAnySubmitted = (project.submissions || []).some(
    (s) => s.is_submitted
  );

  const handleDelete = async () => {
    const warning = hasAnySubmitted
      ? `Delete project "${project.name}"? This includes SUBMITTED content (survey and/or cost sheet). This action cannot be undone.`
      : `Delete project "${project.name}"?`;
    if (!window.confirm(warning)) return;
    setError(null);
    try {
      await adminDeleteProject.mutateAsync({
        endpointId,
        projectId: project.id,
      });
    } catch (err) {
      setError(String(err));
    }
  };

  const xlsxUrl =
    costSub && costSub.is_submitted
      ? `${apiHostname}/survey/${endpointId}/submissions/${costSub.id}/cost-sheet.xlsx`
      : null;
  const surveyPdfUrl =
    surveySub && surveySub.is_submitted && surveySub.pdf_s3_key
      ? `${apiHostname}/survey/${endpointId}/submissions/${surveySub.id}/survey-file/survey.pdf`
      : null;
  const roofPdfUrl =
    surveySub && surveySub.is_submitted
      ? `${apiHostname}/survey/${endpointId}/submissions/${surveySub.id}/survey-file/roof-condition.pdf`
      : null;
  // Admin-only: opens the survey app in an anonymised export mode that
  // regenerates the survey + roof PDFs with the EPC-identifying fields removed
  // and downloads them locally (no stored anonymised copy on the server).
  const anonPdfUrl =
    surveySub && surveySub.is_submitted
      ? `${customerAppDomain}/survey-form.html?apiUrl=${encodeURIComponent(
          apiHostname
        )}&endpointId=${encodeURIComponent(
          endpointId
        )}&name=${encodeURIComponent(
          endpointName
        )}&shortcode=${encodeURIComponent(
          endpointShortcode
        )}&submissionId=${encodeURIComponent(surveySub.id)}&mode=anonExport`
      : null;

  return (
    <tr className="align-middle">
      <td>
        {projectNameHref ? (
          <a href={projectNameHref} target="_blank" rel="noopener noreferrer">
            {project.name}
          </a>
        ) : (
          project.name
        )}
      </td>
      <td>
        {submissionStatusBadge(surveySub)}
        {surveyPdfUrl && (
          <>
            {" "}
            <a
              href={surveyPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="small text-muted ms-1"
            >
              [PDF]
            </a>
          </>
        )}
        {roofPdfUrl && (
          <>
            {" "}
            <a
              href={roofPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="small text-muted ms-1"
            >
              [Roof PDF]
            </a>
          </>
        )}
        {anonPdfUrl && (
          <>
            {" "}
            <a
              href={anonPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="small text-muted ms-1"
              title="Download the survey + roof PDFs with EPC-identifying fields (surveyor name, contact number, PM name + signature) removed. Photos and free-text comments are included unchanged - review before sharing externally."
            >
              [Anon PDF]
            </a>
          </>
        )}
      </td>
      <td>
        {submissionStatusBadge(costSub)}
        {xlsxUrl && costSub && (
          <>
            {" "}
            <CostSheetXlsxLink
              endpointId={endpointId}
              submissionId={costSub.id}
              latestVersion={costSub.latest_version ?? null}
              xlsxUrl={xlsxUrl}
            />
          </>
        )}
        {isImporting ? (
          <span className="text-muted small ms-1">
            <Spinner animation="border" size="sm" /> Importing...
          </span>
        ) : (
          <>
            {" "}
            <label
              htmlFor={`cost-sheet-import-${project.id}`}
              className="small text-muted text-decoration-underline ms-1 mb-0"
              style={{ cursor: "pointer" }}
            >
              [Upload]
            </label>
            <input
              ref={fileInputRef}
              id={`cost-sheet-import-${project.id}`}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={(e) => handleImportFile(e.target.files?.[0])}
            />
          </>
        )}
        {importErrorMessage && (
          <div className="text-danger small mt-1">{importErrorMessage}</div>
        )}
      </td>
      <td style={{ minWidth: selectMinWidth }}>
        <Select
          inputId={`${selectInputIdPrefix}${project.id}`}
          options={siteOptions.map((s) => ({ value: s.id, label: s.name }))}
          value={
            selectedSite
              ? { value: selectedSite.id, label: selectedSite.name }
              : null
          }
          onChange={(next) =>
            handleChange((next as SiteSelectOption | null)?.value ?? null)
          }
          placeholder={
            sitesLoading ? "Loading sites..." : "Search and select a site..."
          }
          isDisabled={sitesLoading || isPending}
          isClearable
          styles={selectStyles}
          menuPortalTarget={document.body}
        />
        {isPending && (
          <div className="text-muted small mt-1">
            <Spinner animation="border" size="sm" /> Updating...
          </div>
        )}
        {error && <div className="text-danger small mt-1">{error}</div>}
      </td>
      <td className="text-muted small">
        {new Date(project.updated_at).toLocaleString()}
      </td>
      <td>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </Button>
        <CostSheetImportReportModal
          projectName={project.name}
          report={importReport}
          version={importVersion}
          onClose={() => setImportReport(null)}
        />
      </td>
    </tr>
  );
};

const formatMoney = (value: number, currency: string): string => {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
};

const versionLabel = (v: CostSheetVersionSummary): string => {
  const perKwp =
    v.total_per_kwp != null
      ? `${formatMoney(v.total_per_kwp, v.currency)}/kWp`
      : "-";
  return `V${v.version_number} - ${formatMoney(v.total, v.currency)} / ${perKwp} - ${new Date(v.submitted_at).toLocaleString("en-GB")}`;
};

// Mirrors the version picker on the customer survey app (survey-app.html):
// a plain link when there is one version, a picker of every version otherwise.
// Versions are fetched only when the picker is opened, so a page of rows
// doesn't fan out one request per row on load.
const CostSheetXlsxLink = ({
  endpointId,
  submissionId,
  latestVersion,
  xlsxUrl,
}: {
  endpointId: string;
  submissionId: string;
  latestVersion: number | null;
  xlsxUrl: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const versionsQuery = useCostSheetVersions(endpointId, submissionId, isOpen);

  if (!latestVersion || latestVersion < 2) {
    return (
      <a
        href={xlsxUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="small text-muted ms-1"
      >
        {latestVersion ? `[XLSX - V${latestVersion}]` : "[XLSX]"}
      </a>
    );
  }

  const versions = versionsQuery.data?.versions ?? [];
  return (
    <Dropdown
      className="d-inline-block ms-1"
      onToggle={(nextShow) => setIsOpen(nextShow)}
    >
      <Dropdown.Toggle
        variant="link"
        size="sm"
        className="p-0 small text-muted align-baseline"
      >
        [XLSX - V{latestVersion}]
      </Dropdown.Toggle>
      {createPortal(
        // Portalled like the row's site Select: the table's scroll wrapper
        // would otherwise clip the menu.
        <Dropdown.Menu
          popperConfig={{ strategy: "fixed" }}
          style={{ zIndex: 9999 }}
        >
          {versionsQuery.isLoading && (
            <Dropdown.ItemText className="text-muted small">
              <Spinner animation="border" size="sm" /> Loading versions...
            </Dropdown.ItemText>
          )}
          {versionsQuery.isError && (
            <Dropdown.ItemText className="text-danger small">
              Could not load versions. Close and reopen to retry.
            </Dropdown.ItemText>
          )}
          {versions.map((v) => (
            <Dropdown.Item
              key={v.id}
              href={`${xlsxUrl}?version=${encodeURIComponent(v.version_number)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="small"
            >
              {versionLabel(v)}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>,
        document.body
      )}
    </Dropdown>
  );
};

const issueContext = (
  section: string | null,
  row: string | null,
  cell: string | null
) => {
  const parts = [section, row, cell].filter((p): p is string => !!p);
  return parts.length ? `${parts.join(" › ")}: ` : "";
};

const CostSheetImportReportModal = ({
  projectName,
  report,
  version,
  onClose,
}: {
  projectName: string;
  report: CostSheetImportReport | null;
  version: CostSheetVersionSummary | null;
  onClose: () => void;
}) => {
  if (!report) return null;
  const errors = report.issues.filter((i) => i.severity === "error");
  const notices = report.issues.filter((i) => i.severity === "notice");
  const perKwp = formatPerKwp(version);
  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Cost sheet import - {projectName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {report.ok ? (
          <div className="alert alert-success">
            Imported{version ? ` V${version.version_number}` : " a new"} cost
            sheet version. {report.rowsImported} row(s) loaded
            {report.customRowsAdded > 0
              ? `, ${report.customRowsAdded} custom row(s) added`
              : ""}
            {report.systemSizeKwp != null
              ? `, system size ${report.systemSizeKwp} kWp`
              : ""}
            {perKwp ? `, ${perKwp}` : ""}.
          </div>
        ) : (
          <div className="alert alert-danger">
            Import failed - nothing was saved. Fix the {errors.length} issue(s)
            below in the spreadsheet and upload again.
          </div>
        )}
        {errors.length > 0 && (
          <>
            <h6 className="text-danger">Errors</h6>
            <ul>
              {errors.map((i, idx) => (
                <li key={`e-${idx}`}>
                  <span className="text-muted">
                    {issueContext(i.section, i.row, i.cell)}
                  </span>
                  {i.message}
                </li>
              ))}
            </ul>
          </>
        )}
        {notices.length > 0 && (
          <>
            <h6 className="text-muted">Notices</h6>
            <ul>
              {notices.map((i, idx) => (
                <li key={`n-${idx}`} className="text-muted">
                  {issueContext(i.section, i.row, i.cell)}
                  {i.message}
                </li>
              ))}
            </ul>
          </>
        )}
        {report.ok && errors.length === 0 && notices.length === 0 && (
          <p className="text-muted mb-0">No issues to report.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
